import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/apiService';

const LicenseContext = createContext();

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (context === undefined) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
};

export const LicenseProvider = ({ children }) => {
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [licenseKey, setLicenseKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState({ role: null, permissions: [] });
  const [userEmail, setUserEmail] = useState(null);
  const [systemId, setSystemId] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [showTrialModal, setShowTrialModal] = useState(false);

  // Initialize license system
  useEffect(() => {
    initializeLicenseSystem();
  }, []);

  const initializeLicenseSystem = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Align trial rows with server (expired → inactive), same as mobile app
      try {
        await apiService.updateAllExpiredTrials();
      } catch (e) {
        console.warn('updateAllExpiredTrials (non-fatal):', e);
      }

      // Use quickLicenseCheck to check for both licenses and active trials
      try {
        const licenseCheck = await apiService.quickLicenseCheck();
        console.log('License check result:', licenseCheck);
        
        if (licenseCheck.isValid) {
          // We have a valid license or active trial
          setIsLicenseValid(true);
          
          if (licenseCheck.isTrial && licenseCheck.trial) {
            // Active trial found
            setTrialStatus(licenseCheck.trial);
            setUserRole({ role: 'trial', permissions: ['humanize', 'script', 'workplan', 'tts'] });
            console.log('Active trial found, license is valid');
            
            // Persist trial activation flag to prevent modal from showing again
            const systemId = apiService.getStoredSystemId() || apiService.generateSystemId();
            localStorage.setItem('trial_activated', 'true');
            localStorage.setItem('trial_system_id', systemId);
            setShowTrialModal(false);
          } else if (licenseCheck.license) {
            // Regular license found
            const storedLicenseKey = apiService.getStoredLicenseKey();
            setLicenseKey(storedLicenseKey);
            setUserRole({ role: 'licensed', permissions: ['all'] });
            console.log('Active license found, license is valid');
            setShowTrialModal(false);
          }
          
          setIsLoading(false);
          return;
        }
      } catch (checkError) {
        console.error('License check error:', checkError);
        // In development, if API check fails but we have a trial activation flag, 
        // assume trial is still valid to prevent modal from showing
        const trialActivatedFlag = localStorage.getItem('trial_activated');
        const trialSystemId = localStorage.getItem('trial_system_id');
        const currentSystemId = apiService.getStoredSystemId() || apiService.generateSystemId();
        const isDevelopment = process.env.NODE_ENV === 'development' || 
                             window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1';
        
        if (isDevelopment && trialActivatedFlag && trialSystemId === currentSystemId) {
          console.log('⚠️ Development mode: API check failed but trial was activated before - assuming valid');
          setIsLicenseValid(true);
          setUserRole({ role: 'trial', permissions: ['humanize', 'script', 'workplan', 'tts'] });
          setShowTrialModal(false);
          setIsLoading(false);
          return;
        }
      }
      
      // No valid license or active trial found
      setIsLicenseValid(false);
      setUserRole({ role: null, permissions: [] });
      
      // Check if trial was already activated before (persist across dev restarts)
      const trialActivatedFlag = localStorage.getItem('trial_activated');
      const trialSystemId = localStorage.getItem('trial_system_id');
      const currentSystemId = apiService.getStoredSystemId() || apiService.generateSystemId();
      
      // If trial was activated with a different systemId (e.g., after clearing data), allow new trial
      const isSameSystem = trialSystemId === currentSystemId;
      
      // Only show modal if:
      // 1. Trial hasn't been activated before, OR
      // 2. Trial was activated but with a different systemId (data was cleared)
      // 3. AND we can use a trial (API check)
      if (!trialActivatedFlag || !isSameSystem) {
        try {
          const canUseTrial = await apiService.canUseTrial();
          console.log('Trial eligibility check:', canUseTrial);
          if (canUseTrial) {
            // Only show modal if trial is available
            setShowTrialModal(true);
          } else {
            console.log('Trial not available - already used or not eligible');
            setShowTrialModal(false);
          }
        } catch (trialError) {
          console.log('Trial check failed:', trialError);
          // In development, if API check fails but we haven't activated a trial, still show modal
          // In production, don't show modal if API check fails
          const isDevelopment = process.env.NODE_ENV === 'development' || 
                               window.location.hostname === 'localhost' || 
                               window.location.hostname === '127.0.0.1';
          if (isDevelopment && !trialActivatedFlag) {
            // Check if user has existing data (forms, work plans, etc.) - if so, don't show modal
            const hasExistingData = 
              localStorage.getItem('recentForms') ||
              localStorage.getItem('workPlans') ||
              localStorage.getItem('openrouter_api_key') ||
              localStorage.getItem('default_ai_model');
            
            if (hasExistingData) {
              console.log('Development mode: User has existing data - skipping trial modal to avoid interruption');
              setShowTrialModal(false);
              // Auto-activate trial silently in dev mode if they have data
              const currentSystemId = apiService.getStoredSystemId() || apiService.generateSystemId();
              localStorage.setItem('trial_activated', 'true');
              localStorage.setItem('trial_system_id', currentSystemId);
              localStorage.setItem('trial_activation_date', new Date().toISOString());
              setIsLicenseValid(true);
              setUserRole({ role: 'trial', permissions: ['humanize', 'script', 'workplan', 'tts'] });
            } else {
              console.log('Development mode: Showing trial modal despite API check failure');
              setShowTrialModal(true);
            }
          } else {
            setShowTrialModal(false);
          }
        }
      } else {
        console.log('Trial was already activated for this system - not showing modal');
        setShowTrialModal(false);
      }
    } catch (error) {
      console.error('License initialization error:', error);
      setError('Failed to initialize license system');
    } finally {
      setIsLoading(false);
    }
  };

  const validateLicense = async (licenseKey) => {
    try {
      const result = await apiService.validateLicense(licenseKey);
      
      if (result.isValid) {
        console.log('License validation successful, updating state...');
        setIsLicenseValid(true);
        setLicenseKey(licenseKey);
        setUserRole({ role: 'licensed', permissions: ['all'] });
        setError(null);
        setShowTrialModal(false);
        setTrialStatus(null); // Clear any trial status
        console.log('State updated - isLicenseValid should be true');
      } else {
        console.log('License validation failed:', result.reason);
        setIsLicenseValid(false);
        setUserRole({ role: null, permissions: [] });
        setError(result.reason || 'Invalid license key');
      }
      
      return result;
    } catch (error) {
      console.error('License validation error:', error);
      setError('License validation failed');
      return { success: false, isValid: false, needsValidation: true, error: error.message };
    }
  };

  const clearLicense = async () => {
    try {
      apiService.clearAllData();
      setLicenseKey(null);
      setIsLicenseValid(false);
      setUserRole({ role: null, permissions: [] });
      setUserEmail(null);
      setTrialStatus(null);
      setError(null);
      
      // Clear trial activation persistence flags
      localStorage.removeItem('trial_activated');
      localStorage.removeItem('trial_system_id');
      localStorage.removeItem('trial_activation_date');
      console.log('✅ Trial activation flags cleared');
    } catch (error) {
      console.error('Error clearing license:', error);
      setError('Failed to clear license');
    }
  };

  const setLicenseKeyValue = (key) => {
    setLicenseKey(key);
    if (key) {
      apiService.storeLicenseKey(key);
    } else {
      apiService.clearStoredLicense();
    }
  };

  const setUserEmailValue = (email) => {
    setUserEmail(email);
    if (email) {
      localStorage.setItem('fiddyscript_user_email', email);
    } else {
      localStorage.removeItem('fiddyscript_user_email');
    }
  };

  const hasPermission = (permission) => {
    return userRole.permissions.includes('all') || userRole.permissions.includes(permission);
  };

  const isAdmin = () => userRole.role === 'admin';
  const isMember = () => userRole.role === 'member';
  const isTrialUser = () => userRole.role === 'trial';

  const loadUserPermissions = () => {
    return Promise.resolve();
  };

  const refreshLicenseStatus = async () => {
    try {
      console.log('Refreshing license status...');
      try {
        await apiService.updateAllExpiredTrials();
      } catch (e) {
        console.warn('updateAllExpiredTrials on refresh (non-fatal):', e);
      }
      const licenseCheck = await apiService.quickLicenseCheck();
      console.log('License refresh result:', licenseCheck);
      
      if (licenseCheck.isValid) {
        // We have a valid license or active trial
        setIsLicenseValid(true);
        
        if (licenseCheck.isTrial && licenseCheck.trial) {
          // Active trial found
          setTrialStatus(licenseCheck.trial);
          setUserRole({ role: 'trial', permissions: ['humanize', 'script', 'workplan', 'tts'] });
          console.log('✅ License refresh: Active trial found');
        } else if (licenseCheck.license) {
          // Regular license found
          const storedLicenseKey = apiService.getStoredLicenseKey();
          setLicenseKey(storedLicenseKey);
          setUserRole({ role: 'licensed', permissions: ['all'] });
          console.log('✅ License refresh: Active license found');
        }
        setError(null);
        return { success: true, isValid: true };
      } else {
        // No valid license or trial
        setIsLicenseValid(false);
        setUserRole({ role: null, permissions: [] });
        console.log('⚠️ License refresh: No valid license or trial found');
        return { success: true, isValid: false };
      }
    } catch (error) {
      console.error('License refresh error:', error);
      setError('Failed to refresh license status');
      return { success: false, error: error.message };
    }
  };

  const activateTrial = async (trialDurationDays = 7) => {
    try {
      console.log('🎯 LicenseContext: Activating trial...');
      const trialLicense = await apiService.activateTrial(trialDurationDays);
      console.log('✅ LicenseContext: Trial activation API call successful:', trialLicense);
      
      // CRITICAL: Verify trial was actually created
      if (!trialLicense || !trialLicense.id) {
        console.error('❌ LicenseContext: Trial activation returned invalid data', {
          hasTrialLicense: !!trialLicense,
          trialKeys: trialLicense ? Object.keys(trialLicense) : []
        });
        throw new Error('Trial activation failed: Invalid response from server');
      }
      
      // Immediately set state optimistically - DON'T reset this even if verification fails
      setTrialStatus(trialLicense);
      setIsLicenseValid(true);
      setUserRole({ role: 'trial', permissions: ['humanize', 'script', 'workplan', 'tts'] });
      setShowTrialModal(false);
      setError(null);
      
      // Persist trial activation flag to prevent modal from showing again in development
      const systemId = apiService.getStoredSystemId() || apiService.generateSystemId();
      localStorage.setItem('trial_activated', 'true');
      localStorage.setItem('trial_system_id', systemId);
      localStorage.setItem('trial_activation_date', new Date().toISOString());
      console.log('✅ LicenseContext: Trial activation persisted to localStorage');
      
      console.log('✅ LicenseContext: State updated optimistically - trial is valid');
      
      // Verify from API to ensure state is synchronized (but don't reset state if verification fails)
      // Retry a few times in case database hasn't updated yet
      let retries = 3;
      let verified = false;
      
      while (retries > 0 && !verified) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms between retries
        console.log(`🔍 LicenseContext: Verifying trial activation from API (${4 - retries}/3)...`);
        
        try {
          const licenseCheck = await apiService.quickLicenseCheck();
          console.log('🔍 LicenseContext: quickLicenseCheck result:', licenseCheck);
          
          if (licenseCheck.isValid && licenseCheck.isTrial && licenseCheck.trial) {
            verified = true;
            // Update state with verified trial data
            setTrialStatus(licenseCheck.trial);
            console.log('✅ LicenseContext: Trial verified successfully from API');
          } else {
            retries--;
            if (retries > 0) {
              console.log(`⚠️ LicenseContext: Trial not yet verified, retrying... (${retries} attempts left)`);
            } else {
              console.warn('⚠️ LicenseContext: Could not verify trial from API after retries, but keeping optimistic state');
            }
          }
        } catch (verifyError) {
          console.error('❌ LicenseContext: Error during verification:', verifyError);
          retries--;
        }
      }
      
      if (!verified) {
        console.warn('⚠️ LicenseContext: Could not verify trial from API, but keeping optimistic state. Trial was activated successfully.');
      }
      
      return { success: true, trialLicense };
    } catch (error) {
      console.error('❌ LicenseContext: Trial activation error:', error);
      setError(error.message || 'Failed to activate trial');
      // Don't reset isLicenseValid on error - let user retry
      return { success: false, error: error.message };
    }
  };

  const checkFeatureAccess = async (feature) => {
    try {
      return await apiService.checkFeatureAccess(feature);
    } catch (error) {
      console.error('Error checking feature access:', error);
      return { hasAccess: false, reason: 'Error checking access' };
    }
  };

  const incrementUsage = async (feature) => {
    try {
      return await apiService.incrementUsage(feature);
    } catch (error) {
      console.error('Error incrementing usage:', error);
      return false;
    }
  };

  const getTrialInfo = async () => {
    try {
      return await apiService.getTrialInfo();
    } catch (error) {
      console.error('Error getting trial info:', error);
      return null;
    }
  };

  const showTrialModalHandler = () => {
    setShowTrialModal(true);
  };

  const hideTrialModalHandler = () => {
    setShowTrialModal(false);
  };

  return (
    <LicenseContext.Provider
      value={{
        isLicenseValid,
        licenseKey,
        isLoading,
        error,
        userRole,
        userEmail,
        validateLicense,
        clearLicense,
        setLicenseKey: setLicenseKeyValue,
        setUserEmail: setUserEmailValue,
        hasPermission,
        isAdmin,
        isMember,
        loadUserPermissions,
        showTrialModal,
        hideTrialModal: hideTrialModalHandler,
        activateTrial,
        refreshLicenseStatus,
        checkFeatureAccess,
        incrementUsage,
        isTrialUser,
        getTrialInfo,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
};
